'use client';

import React, { useState } from 'react';
import { stripHtml } from 'src/utils/stripHtml';
import { BaseSelectableItem, GenericNodeComponentProps } from '../types';

function GenericNodeComponent<T extends BaseSelectableItem>({
  config,
  items,
  connector,
  onRemoveItem,
  onAddMore,
  onConnectorChange,
}: GenericNodeComponentProps<T>) {
  const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);
  const [showAllItems, setShowAllItems] = useState(false);

  const {
    colorTheme,
    getItemLabel,
    getItemKey,
    maxVisibleItems = 2,
    showConnectorLabel = true,
  } = config;

  const textColor = `text-${colorTheme}-700`;
  const hoverTextColor = `hover:text-${colorTheme}-900`;

  // Filter out any null/undefined items that may have slipped through
  const validItems = items?.filter((item) => item != null) ?? [];

  if (validItems.length === 0) return null;

  if (validItems.length === 1) {
    const item = validItems[0];
    const itemKey = getItemKey(item);
    const label = getItemLabel(item);

    return (
      <span
        className={`inline-flex items-center cursor-pointer group ${hoverTextColor} transition-colors`}
        onMouseEnter={() => setHoveredItemKey(itemKey)}
        onMouseLeave={() => setHoveredItemKey(null)}
        onClick={onAddMore}
      >
        <span className={`${textColor} text-sm font-medium`}>
          {stripHtml(label)}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemoveItem(item);
          }}
          className={`text-xs ml-1 transition-opacity ${
            hoveredItemKey === itemKey
              ? 'text-red-500 hover:text-red-700 opacity-100'
              : 'text-gray-400 opacity-0 pointer-events-none'
          }`}
          aria-label={`Remove ${stripHtml(label)}`}
        >
          ×
        </button>
      </span>
    );
  }

  const itemsToShow = showAllItems
    ? validItems
    : validItems.slice(0, maxVisibleItems);
  const connectorLabel = connector === 'ANY' ? 'or' : 'and';

  return (
    <span className="inline-flex items-center group flex-wrap">
      {itemsToShow.map((item, index) => {
        const itemKey = getItemKey(item);
        const label = getItemLabel(item);

        return (
          <React.Fragment key={itemKey}>
            <span
              className={`inline-flex items-center cursor-pointer ${hoverTextColor} transition-colors`}
              onMouseEnter={() => setHoveredItemKey(itemKey)}
              onMouseLeave={() => setHoveredItemKey(null)}
              onClick={onAddMore}
            >
              <span className={`${textColor} text-sm font-medium`}>
                {stripHtml(label)}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveItem(item);
                }}
                className={`text-xs ml-1 transition-opacity ${
                  hoveredItemKey === itemKey
                    ? 'text-red-500 hover:text-red-700 opacity-100'
                    : 'text-gray-400 opacity-0 pointer-events-none'
                }`}
                aria-label={`Remove ${stripHtml(label)}`}
              >
                ×
              </button>
            </span>
            {showConnectorLabel && index < itemsToShow.length - 1 && (
              <span
                className={`text-gray-700 text-sm mx-1 ${
                  onConnectorChange
                    ? 'cursor-pointer hover:text-sky-600 hover:underline'
                    : ''
                }`}
                onClick={
                  onConnectorChange
                    ? (e) => {
                        e.stopPropagation();
                        onConnectorChange(connector === 'ANY' ? 'ALL' : 'ANY');
                      }
                    : undefined
                }
                title={
                  onConnectorChange
                    ? `Click to toggle between 'or' and 'and'`
                    : undefined
                }
              >
                {connectorLabel}
              </span>
            )}
          </React.Fragment>
        );
      })}

      {validItems.length > maxVisibleItems && !showAllItems && (
        <button
          onClick={() => setShowAllItems(true)}
          className={`inline-flex items-center ml-1 text-sm ${textColor} hover:underline transition-colors`}
          aria-label={`Show ${validItems.length - maxVisibleItems} more`}
        >
          +{validItems.length - maxVisibleItems} more
        </button>
      )}

      {validItems.length > maxVisibleItems && showAllItems && (
        <button
          onClick={() => setShowAllItems(false)}
          className={`inline-flex items-center ml-1 text-sm ${textColor} hover:underline transition-colors`}
          aria-label="Show less"
        >
          show less
        </button>
      )}
    </span>
  );
}

export default GenericNodeComponent;
